import { Upload } from 'antd';
import Button from '../core/Button';
import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router';
import Select from '../basic/SelectField';
import Constants from '../../Constants';
import { getEmbedUrl } from '../../functions';
import Loading from '../basic/Loading';
import { downloadFromS3Link } from '../../helpers/downloads';
import { Box, Text, Stack, Input, HStack } from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Datetime from '@/components/core/DatePicker';
import { selectArticleRegions, fetchArticleRegions } from '../../slices/article-region/articleRegionSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Field } from '@/components/ui/field';

const ArticleEdit = ({
  tags,
  deleteArticle,
  saveArticle,
  getTags,
  currentArticle,
  token,
  userFullName,
  saving,
  saved,
  article,
  resetEditStatus,
  isKpi = false,
  organisations,
  getOrganisations,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { editedArticle } = location.state || {};
  const initCurrentArticle = editedArticle || {
    author: userFullName,
    title: '',
    content: '',
    hook: '',
    hero_url: '',
    attachment_url: '',
    image_urls: [],
    tags: isKpi ? ['KPI'] : [],
    region: isKpi ? useParams().region : [],
    published_date: new Date().toISOString(),
    read_access: [],
  };
  const [currentArticleState, setCurrentArticleState] = useState(initCurrentArticle);
  const [currentContent, setCurrentContent] = useState('');
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const articleRegions = useSelector(selectArticleRegions);
  const { errors, setFieldValue, handleSubmit } = useFormik({
    initialValues: initCurrentArticle,
    validationSchema: Yup.object().shape({
      region: Yup.string().nullable().required('Required'),
      published_date: Yup.string().nullable().required('Required'),
    }),
    onSubmit: async () => {
      const thisArticle = { ...currentArticleState };
      thisArticle.content = currentContent || '';
      await saveArticle(thisArticle);
      navigate(isKpi ? '/kpi' : '/articles');
    },
  });
  useEffect(() => {
    getOrganisations(token);
    getTags(token);
    dispatch(fetchArticleRegions());
  }, []);

  useEffect(() => {
    if (editedArticle) {
      setCurrentArticleState({
        ...editedArticle,
      });
      setCurrentContent(editedArticle.content || '');
    }
  }, [editedArticle]);

  const onEditorStateChange = e => {
    setCurrentContent(e.target.value);
  };

  const inputEdited = (type, value) => {
    setCurrentArticleState(prevArticleState => ({ ...prevArticleState, [type]: value }));
  };

  const onDelete = async () => {
    await deleteArticle(currentArticleState);
    window.history.replaceState({}, document.title);
    history.replace(isKpi ? '/kpi' : '/articles');
  };

  const organisationOptions = [...organisations].reduce((agg, org) => {
    agg.push({
      label: `${org.uid} - ${org.name}`,
      value: org.id,
    });
    return agg;
  }, []);

  const readAccessOrgs = [];
  currentArticleState.read_access?.forEach(id => {
    readAccessOrgs.push(organisations.filter(o => o.id === id)[0]);
  });
  const uploadImage = (file, uploadType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('model_name', 'article');
    const header = new Headers({ Authorization: `Token ${token}` }, { 'Content-Type': 'multipart/form-data' });
    return fetch(`${Constants.URL}upload_image_file`, {
      method: 'POST',
      headers: header,
      body: formData,
    })
      .then(response => response.json())
      .then(json => {
        const newArticle = { ...currentArticleState };
        if (uploadType === 'hero') {
          newArticle.hero_url = json.link;
          setCurrentArticleState(newArticle);
          setUploadingHero(false);
        } else {
          inputEdited('image_urls', [...newArticle.image_urls, json.link]);
        }

        return {
          data: {
            link: json.link,
          },
        };
      })
      .catch(() => {});
  };

  const uploadAttachment = file => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('model_name', 'article');
    formData.append('file_type', 'file');
    const header = new Headers({ Authorization: `Token ${token}` });
    return fetch(`${Constants.URL}upload_image_file`, {
      method: 'POST',
      headers: header,
      body: formData,
    })
      .then(response => response.json())
      .then(json => {
        const newArticle = { ...currentArticleState };
        newArticle.attachment_url = json.link;
        setCurrentArticleState(newArticle);
        setUploadingAttachment(false);
        return {
          data: {
            link: json.link,
          },
        };
      })
      .catch(() => {});
  };
  const embedCallback = url => {
    try {
      return getEmbedUrl(url);
    } catch (err) {
      // No required
    }
    return null;
  };

  if (saving === true) {
    return <Loading />;
  }
  if (saved === true) {
    return (
      <Box p="20">
        <Text mb="10">
          Your {isKpi ? 'kpi' : 'article'} has been {!currentArticle ? 'created' : 'updated'}.
        </Text>
        <Link to={`/${isKpi ? `kpi/${currentArticleState.region}` : 'articles'}/${article.slug}`}>
          <Button onClick={resetEditStatus}>View it here</Button>
        </Link>
      </Box>
    );
  }
  return (
    <form onSubmit={handleSubmit}>
      <Box p="10" background="white">
        <Stack gap="4">
          <Field id="title" label="Title">
            <Input
              type="text"
              value={currentArticleState.title}
              placeholder="Article Title"
              onChange={e => inputEdited('title', e.target.value)}
            />
          </Field>
          <Field
            id="hook"
            label="Hook"
            optionalText={`${currentArticleState?.hook?.length}/${Constants.ARTICLE_HOOK_MAX_LEN}`}
          >
            <Input
              type="text"
              value={currentArticleState.hook}
              placeholder="A short phrase used to entice the reader."
              onChange={e => inputEdited('hook', e.target.value)}
              maxLength={Constants.ARTICLE_HOOK_MAX_LEN}
            />
          </Field>
          <Field id="author" label="Author">
            <Input
              type="text"
              value={currentArticleState.author}
              placeholder="Author"
              onChange={e => inputEdited('author', e.target.value)}
            />
          </Field>
          <Field id="tags" label="Tags">
            <Select
              {...{
                isClearable: true,
                isSearchable: true,
                closeMenuOnSelect: false,
                removeSelected: true,
                isMulti: true,
                name: 'select-tags',
                value: currentArticleState.tags ? currentArticleState.tags.map(l => ({ label: l, value: l })) : [],
                onChange: e =>
                  inputEdited(
                    'tags',
                    e.map(tg => tg.value)
                  ),
                options: tags.map(l => ({ label: l.value, value: l.value })),
              }}
            />
          </Field>
          <Field invalid={errors.region} label="Region" errorText={errors.region}>
            <Select
              id="region"
              name="region"
              value={
                currentArticleState.region
                  ? { label: currentArticleState.region, value: currentArticleState.region }
                  : currentArticleState.region
              }
              options={articleRegions.map(l => ({ label: l.name, value: l.name }))}
              placeholder="Region"
              onChange={option => {
                inputEdited('region', option.value);
                setFieldValue('region', option.value);
              }}
            />
            {/* {errors.region && <Alert style={{ marginTop: '5px' }} message={errors.region} type="error" />} */}
          </Field>
          <Field id="read_access" label="Read Access">
            <Select
              {...{
                name: 'select-tags',
                value:
                  currentArticleState.read_access && readAccessOrgs.length > 0
                    ? readAccessOrgs.reduce((agg, org) => {
                        agg.push({
                          label: `${org?.uid} - ${org?.name}`,
                          value: org?.id,
                        });
                        return agg;
                      }, [])
                    : [],
                options: organisationOptions,
                isMulti: true,
                onChange: e =>
                  inputEdited(
                    'read_access',
                    e.map(acc => acc.value)
                  ),
              }}
            />
          </Field>
          <Field invalid={errors.published_date} label="Published Date" errorText={errors.published_date}>
            <Datetime
              id="published_date"
              inputProps={{
                placeholder: 'Creation Date',
                readOnly: true,
              }}
              closeOnSelect
              value={new Date(currentArticleState.published_date)}
              onChange={e => {
                inputEdited('published_date', e);
                setFieldValue('published_date', e);
              }}
              timeFormat="hh:mm a"
              dateFormat="YYYY-MM-DD"
              className="w-full"
            />
          </Field>
          <Field id="hero-url" label="Hero Image">
            <Upload
              name="hero-url"
              showUploadList={false}
              customRequest={({ file }) => uploadImage(file, 'hero')}
              multiple={false}
              onChange={info => {
                if (info.file.status === 'uploading') {
                  setUploadingHero(true);
                }
              }}
            >
              {uploadingHero && <div>Loading...</div>}
              {!uploadingHero && currentArticleState.hero_url && (
                <img alt="Hero" style={{ width: '100%' }} src={currentArticleState.hero_url} />
              )}
              {!uploadingHero && !currentArticleState.hero_url && (
                <Box background="neutral.8" p="10">
                  Click or drag an image over this box to upload
                </Box>
              )}
            </Upload>
          </Field>
          <Field id="file-attachment" label="KPI Attachment">
            <Upload
              name="file-attachment"
              showUploadList={false}
              customRequest={({ file }) => uploadAttachment(file)}
              multiple={false}
              onChange={info => {
                if (info.file.status === 'uploading') {
                  setUploadingAttachment(true);
                }
              }}
            >
              {uploadingAttachment ? (
                <div>Loading...</div>
              ) : (
                <HStack>
                  {currentArticleState.attachment_url && (
                    <Button
                      type="link"
                      onClick={e => {
                        e.stopPropagation();
                        downloadFromS3Link(currentArticleState.attachment_url);
                      }}
                    >
                      Download
                    </Button>
                  )}
                  <Button>Upload Attachment</Button>
                </HStack>
              )}
            </Upload>
          </Field>
          <Field id="content" label="Content">
            <textarea
              value={currentContent}
              onChange={onEditorStateChange}
              className="form-control"
              placeholder="Write content (HTML allowed if supported by your backend)"
              rows={12}
            />
          </Field>
        </Stack>
        <HStack>
          <Button type="submit" colorScheme="actionSecondary" mt="5">
            Submit
          </Button>

          <Button colorScheme="red" variant="outline" mt="5" onClick={onDelete}>
            Delete
          </Button>
        </HStack>
      </Box>
    </form>
  );
};

ArticleEdit.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.string, value: PropTypes.string })),
  saveArticle: PropTypes.func,
  getTags: PropTypes.func,
  deleteArticle: PropTypes.func,
  currentArticle: PropTypes.shape({
    content: PropTypes.string,
    published_date: PropTypes.string,
    created: PropTypes.string,
  }),
  token: PropTypes.string,
  userFullName: PropTypes.string,
  saving: PropTypes.bool,
  saved: PropTypes.bool,
  article: PropTypes.shape({ slug: PropTypes.string }),
  resetEditStatus: PropTypes.func,
  isKpi: PropTypes.bool,
  getOrganisations: PropTypes.func,
  organisations: PropTypes.arrayOf(
    PropTypes.objectOf(
      { type: PropTypes.string, value: PropTypes.string },
      { type: PropTypes.integer, value: PropTypes.integer }
    )
  ),
};

export default ArticleEdit;
